import { nextAvailableStreetName, type Street } from "../model/inp/streets.ts";
import { useT } from "../i18n/hooks.ts";
import { NumberInput } from "./fields.tsx";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";

export function StreetsEditor({
  streets,
  onChange,
  selectedName,
  onSelect,
}: {
  streets: Street[];
  onChange: (streets: Street[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deleteStreet,
    rename: renameSelected,
    update,
  } = useNamedCollection(streets, onChange, selectedName, onSelect);

  function addStreet() {
    const name = nextAvailableStreetName(new Set(streets.map((s) => s.name)));
    add({
      name,
      roadWidth: 20,
      curbHeight: 0.5,
      crossSlope: 2,
      roughness: 0.016,
      gutterDepression: 0,
      gutterWidth: 0,
      sides: 2,
      backingWidth: 0,
      backingSlope: 0,
      backingRoughness: 0,
    });
  }

  function set<K extends keyof Street>(key: K, value: Street[K]) {
    update((s) => ({ ...s, [key]: value }));
  }

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={streets}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addStreet}
        addLabel={t("projectData.addStreet")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("projectData.noStreetSelected")}</p>}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deleteStreet(selected.name)}
              deleteLabel={t("projectData.deleteStreet")}
            />
            <div className="named-collection-fields">
              <label className="options-field">
                {t("projectData.streetRoadWidth")}
                <NumberInput value={selected.roadWidth} onChange={(v) => set("roadWidth", v)} />
              </label>
              <label className="options-field">
                {t("projectData.streetCurbHeight")}
                <NumberInput value={selected.curbHeight} onChange={(v) => set("curbHeight", v)} />
              </label>
              <label className="options-field">
                {t("projectData.streetCrossSlope")}
                <NumberInput value={selected.crossSlope} onChange={(v) => set("crossSlope", v)} />
              </label>
              <label className="options-field">
                {t("projectData.streetRoughness")}
                <NumberInput value={selected.roughness} onChange={(v) => set("roughness", v)} />
              </label>
              <label className="options-field">
                {t("projectData.streetGutterDepression")}
                <NumberInput
                  value={selected.gutterDepression}
                  onChange={(v) => set("gutterDepression", v)}
                />
              </label>
              <label className="options-field">
                {t("projectData.streetGutterWidth")}
                <NumberInput value={selected.gutterWidth} onChange={(v) => set("gutterWidth", v)} />
              </label>
              <label className="options-field">
                {t("projectData.streetSides")}
                <select
                  value={selected.sides}
                  onChange={(e) => set("sides", Number(e.target.value) === 1 ? 1 : 2)}
                >
                  <option value={2}>{t("projectData.streetTwoSided")}</option>
                  <option value={1}>{t("projectData.streetOneSided")}</option>
                </select>
              </label>
              <label className="options-field">
                {t("projectData.streetBackingWidth")}
                <NumberInput
                  value={selected.backingWidth}
                  onChange={(v) => set("backingWidth", v)}
                />
              </label>
              <label className="options-field">
                {t("projectData.streetBackingSlope")}
                <NumberInput
                  value={selected.backingSlope}
                  onChange={(v) => set("backingSlope", v)}
                />
              </label>
              <label className="options-field">
                {t("projectData.streetBackingRoughness")}
                <NumberInput
                  value={selected.backingRoughness}
                  onChange={(v) => set("backingRoughness", v)}
                />
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
